import { useEffect, useRef } from 'react';

// Минимальный масштаб: ниже него текст становится нечитаемым.
const MIN_SCALE = 0.45;
// Максимальный масштаб = 1: навбар только ужимается под низкий экран, но никогда
// не увеличивается выше базовых размеров (иначе пункты «раздувались»).
const MAX_SCALE = 1;
// Кол-во кадров после монтирования, в течение которых пересчитываем масштаб —
// чтобы поймать догрузку шрифтов/иконок, влияющую на высоту контента.
const SETTLE_FRAMES = 8;

/**
 * Подгоняет навигацию под доступную высоту, чтобы все пункты всегда влезали без
 * скролла на экране любой высоты (адаптация под разные экраны).
 *
 * Важно: пересчёт происходит ТОЛЬКО при изменении доступного места (ресайз окна,
 * догрузка шрифтов), но НЕ при сворачивании/разворачивании секций. Поэтому при
 * сворачивании размеры пунктов не меняются — освободившееся место просто
 * остаётся пустым, а масштаб остаётся таким, чтобы вмещался полный (раскрытый)
 * список.
 */
export function useNavFitScale() {
  const navRef = useRef<HTMLElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const apply = () => {
    const nav = navRef.current;
    const content = contentRef.current;
    if (!nav || !content) {
      return;
    }

    // Ставим переменную на :root, чтобы она каскадировала и на элементы вне
    // поддерева навбара (выпадашки в портале, чипы фильтра на странице).
    const root = document.documentElement;

    // Меряем «натуральную» высоту контента при масштабе 1, затем выставляем
    // итоговый масштаб. Сброс до 1 виден измерению, но не пользователю.
    root.style.setProperty('--nav-scale', '1');
    const natural = content.scrollHeight;
    const available = nav.clientHeight;
    if (!natural || !available) {
      return;
    }

    const scale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, available / natural));
    root.style.setProperty('--nav-scale', scale.toFixed(4));
  };

  // Пересчёт при изменении доступного места: ресайз окна/контейнера и догрузка
  // шрифтов. На сворачивание секций НЕ реагируем намеренно.
  useEffect(() => {
    const nav = navRef.current;
    if (!nav) {
      return;
    }

    let raf = 0;
    const schedule = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(apply);
    };

    // Несколько кадров после монтирования — поймать догрузку шрифтов/иконок.
    let frame = 0;
    let settleRaf = requestAnimationFrame(function tick() {
      apply();
      frame += 1;
      if (frame < SETTLE_FRAMES) {
        settleRaf = requestAnimationFrame(tick);
      }
    });

    const observer = new ResizeObserver(schedule);
    observer.observe(nav);
    window.addEventListener('resize', schedule);
    document.fonts?.ready.then(schedule).catch(() => undefined);

    return () => {
      cancelAnimationFrame(raf);
      cancelAnimationFrame(settleRaf);
      observer.disconnect();
      window.removeEventListener('resize', schedule);
    };
  }, []);

  return { navRef, contentRef };
}
